import React, { useEffect, useState } from "react";
import { apiDelete, apiGet, apiPost } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import useToastContext from "hooks/useToastContext";
import ConfirmationModal from "components/reusable/ConfirmationModal";

const EmployeeIpModal = ({ item, onClose }) => {
  const notification = useToastContext();
  const employeeId = item?.Employee?._id || item?._id;
  const [ips, setIps] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [employee, setEmployee] = useState(item?.Employee || item);
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(null);

  const load = async () => {
    const ipRes = await apiGet(`${apiPath.hrGeoEmployeeIps}/${employeeId}/ips`);
    if (ipRes?.data?.success) {
      setIps(ipRes.data.results?.ips || []);
      setBlockedIps(ipRes.data.results?.blockedIps || []);
      if (ipRes.data.results?.employee) setEmployee(ipRes.data.results.employee);
    } else {
      notification.error(ipRes?.data?.message);
    }
  };

  useEffect(() => {
    if (employeeId) load();
  }, [employeeId]);

  const saveEmployeeIp = async (ip, ipLabel, blocked) => {
    setSaving(true);
    const res = await apiPost(`${apiPath.hrGeoEmployeeIps}/${employeeId}/ips`, {
      value: ip,
      label: ipLabel,
      blocked: !!blocked,
    });
    setSaving(false);
    if (res?.data?.success) {
      notification.success(
        blocked
          ? "IP blocked for this employee"
          : res.data.message || "IP added for this employee",
      );
      setValue("");
      setLabel("");
      load();
      return true;
    }
    notification.error(res?.data?.message || "Unable to save IP");
    return false;
  };

  const addIp = async (e) => {
    e.preventDefault();
    const ip = value.trim();
    if (!ip) return;
    await saveEmployeeIp(ip, label.trim() || "Work from home", false);
  };

  const blockIp = async (rawValue, rawLabel) => {
    const ip = String(rawValue || value).trim();
    if (!ip) return;
    await saveEmployeeIp(
      ip,
      String(rawLabel || label).trim() || "Blocked employee IP",
      true,
    );
  };

  const removeIp = async (ipId) => {
    const res = await apiDelete(
      `${apiPath.hrGeoEmployeeIps}/${employeeId}/ips/${ipId}`,
    );
    if (res?.data?.success) {
      notification.success(res.data.message);
      load();
    } else {
      notification.error(res?.data?.message);
    }
  };

  return (
    <div className="overflow-x-hidden overflow-y-auto fixed inset-0 justify-center items-center flex z-50 outline-none focus:outline-none bg-black/40">
      <div className="relative mx-auto max-w-lg w-full my-6 px-3">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">
              Employee IPs — {employee?.name || employee?.email}
            </h3>
            <button type="button" className="text-2xl leading-none" onClick={onClose}>
              ×
            </button>
          </div>
          <form onSubmit={addIp} className="p-4 flex flex-wrap gap-2">
            <p className="w-full text-sm text-gray-500 mb-1">
              Add or block this employee’s IP. HR and Accountant share the same
              list. Blocking here does not log out HR or accountant.
            </p>
            <input
              className="border rounded-lg px-3 py-2 flex-1 min-w-[160px]"
              placeholder="Employee IP or CIDR (home Wi-Fi)"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <input
              className="border rounded-lg px-3 py-2 flex-1 min-w-[120px]"
              placeholder="Label (work from home)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <button type="submit" className="bg-LightBlue text-white px-4 py-2 rounded-lg" disabled={saving}>
              Add IP
            </button>
            <button
              type="button"
              className="border border-red-400 text-red-600 px-4 py-2 rounded-lg"
              disabled={saving}
              onClick={() => blockIp(value, label)}
            >
              Block IP
            </button>
          </form>
          <div className="px-4 pb-4">
            {(ips || []).length === 0 ? (
              <p className="text-sm text-gray-500">
                No personal IPs yet. If they are not on a company IP, they cannot
                login until you add their work-from-home IP here.
              </p>
            ) : (
              ips.map((ip) => (
                <div
                  key={ip._id}
                  className="flex justify-between items-center border-b py-2 text-sm"
                >
                  <span>
                    {ip.value} {ip.label ? `— ${ip.label}` : ""}
                  </span>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="text-red-600"
                      onClick={() => blockIp(ip.value, ip.label)}
                    >
                      Block
                    </button>
                    <button
                      type="button"
                      className="text-red-500"
                      onClick={() =>
                        setRemoveConfirm({
                          name: ip.value,
                          fromList: "Allowed",
                          run: () => removeIp(ip._id),
                        })
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}
            <h4 className="font-semibold mt-4 mb-1">Blocked IPs</h4>
            <p className="text-sm text-gray-500 mb-2">
              These IPs cannot login for this employee. HR and accountant login
              are not affected.
            </p>
            {(blockedIps || []).length === 0 ? (
              <p className="text-sm text-gray-400">None</p>
            ) : (
              (blockedIps || []).map((ip) => (
                <div
                  key={`block-${ip._id || ip.value}`}
                  className="flex justify-between items-center border-b py-2 text-sm"
                >
                  <span>
                    {ip.value} {ip.label ? `— ${ip.label}` : ""}
                  </span>
                  <button
                    type="button"
                    className="text-red-500"
                    onClick={() =>
                      setRemoveConfirm({
                        name: ip.value,
                        fromList: "Blocked",
                        run: () => removeIp(ip._id),
                      })
                    }
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={!!removeConfirm}
        onClose={() => setRemoveConfirm(null)}
        onConfirm={() => {
          removeConfirm?.run?.();
          setRemoveConfirm(null);
        }}
        isDanger
        title="Remove?"
        message={`Do you still want to remove ${removeConfirm?.name || "this"}${
          removeConfirm?.fromList ? ` from ${removeConfirm.fromList}` : ""
        }?`}
        confirmText="Yes, remove"
        cancelText="Cancel"
      />
    </div>
  );
};

export default EmployeeIpModal;
